import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _ from 'lodash';

import { Grid } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import { Info as InfoIcon } from '@material-ui/icons';

import * as strings from '../../helpers/strings';
import * as statisticsReducer from '../reducer';
import * as statisticsActionMgr from '../action-mgr';
import StationStatisticsTotalize from './StationStatisticsTotalize'; 

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { gradientColors } from '../../helpers/constants';

import '../../index.css';

const sortSetting = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

class StationStatisticsGraph extends Component {
    constructor(props) {
        super(props);
        this.state = {
            options: {
                title: {
                    text: '',
                },
                plotOptions: {
                    column: {
                        grouping: true,
                        stacking: 'normal', // Enable stacking
                        groupPadding: 0.42, // control group spacing
                        pointPadding: 0.40, // control bar spacing
                        pointWidth: 55,
                        dataLabels: {
                            enabled: true,
                            formatter: function () {
                                return this.y.toLocaleString(); // Format data labels with commas
                            }
                        }
                    },
                },
                credits: {
                    enabled: false,
                },
                exporting: {
                    enabled: true,
                    buttons: {
                    contextButton: {
                        menuItems: ['viewFullscreen'] // only this item will show
                    }
                    }
                }
            },
            facilitySeries: [],
            isolationSeries: [],
        };
    }

    componentDidMount() {
        this.initChart();
    }

    componentDidUpdate(prevProps) {
        const { selectedStationName, statisticsFilter, otherData } = this.props;
        // Re-fetch or re-initialize graph data if the station, filter, or data has changed
        if (
            prevProps.selectedStationName !== selectedStationName ||
            prevProps.statisticsFilter !== statisticsFilter ||
            prevProps.otherData !== otherData
        ) {
            this.initChart(); // Assuming initChart is the method that initializes the graph
        }
    }

    initChart = () => {
        const { otherData, statisticsFilter } = this.props;
    
        if (!otherData) {
            return;
        }
    
        let cloneFacilitySeries = _.cloneDeep(this.state.facilitySeries);
        if (otherData) {
            let sortFacility = otherData.slice().sort((a, b) => a.facility.localeCompare(b.facility, undefined, { numeric: true }));
            let facilityGroup = _.groupBy(sortFacility, d => d.facility);
            
            cloneFacilitySeries = Object.entries(facilityGroup).map(([f_key, f_value]) => ({
                name: f_key,
                data: f_value.map(v => {
                    const timeValue = statisticsFilter.time === "all" ? v.time : v.time.split("-")[1]; // Use month in monthly view
                    const dataPoint = {
                        x: timeValue,
                        y: v.energyYields,
                        performanceRatio: v.performanceRatio,
                        systemCapacity: v.systemCapacity, // Use individual system capacity for each facility
                        insolation: v.insolation
                    };
                    return dataPoint;
                })
            }));
        }
    
        let cloneIsolationSeries = _.cloneDeep(this.state.isolationSeries);
        if (otherData) {
            let isolationGroup = _.uniqBy(otherData, d => d.time);
            cloneIsolationSeries = [{
                name: "Insolation",
                color: '#E02E44',
                type: 'line',
                data: isolationGroup.map((i) => ({
                    x: parseInt((statisticsFilter.time === "all") ? i.time : i.time.split("-")[1]),
                    y: i.insolation
                }))
            }];
        }
    
        this.setState({
            facilitySeries: cloneFacilitySeries,
            isolationSeries: cloneIsolationSeries,
        });
    }
    

    getGradientColor(index) {
        const gradientColor = gradientColors[index % gradientColors.length];
        return {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
                [0, gradientColor[0]], // light color
                [1, gradientColor[1]]  // dark color
            ]
        };
    }

    render() {
        const { statisticsFilter } = this.props;
        const yAxisTitle = (statisticsFilter.time === "all") ? "kWh/m²/year" : "kWh/m²/month";

        return (
            <Grid container alignItems="center">
                <Grid item xs={12} style={{ margin: '1px 10px'}}>
                    {(this.state.facilitySeries.length > 0 || this.state.isolationSeries.length > 0) && (
                        <Paper style={{ paddingTop: 15 }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    ...this.state.options,
                                    chart: {
                                        height: 650,
                                    },
                                    xAxis: {
                                        categories: ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                                        allowDecimals: false,
                                        title: {
                                            text: (statisticsFilter.time === "all") ? "Year" : "Months"
                                        }
                                    },
                                    yAxis: [
                                        { // Primary yAxis for energy meters
                                            title: {
                                                text: "kWh",
                                            },
                                            labels: {
                                                formatter: function () {
                                                    return this.value.toLocaleString(); // Format yAxis labels with commas
                                                }
                                            },
                                            stackLabels: {
                                                enabled: true,
                                                style: {
                                                    fontWeight: 'bold',
                                                    color: 'gray',
                                                    textOutline: 'none',
                                                    marginBottom: '30px'
                                                },
                                                formatter: function () {
                                                    let groupedData = {};
                                                    const currentTime = this.x; // Get the time for the current bar being hovered over
                                                
                                                    // Group data by year-month and set system capacity once for the selected facilities
                                                    this.axis.chart.series.forEach((series) => {
                                                        if (series.type === 'column') {
                                                            series.userOptions.data.forEach((dataPoint) => {
                                                                // Check for missing properties
                                                                if (!dataPoint.x || dataPoint.y === undefined) {
                                                                    console.warn('Data point missing time or energyYields:', dataPoint);
                                                                    return; // Skip this data point
                                                                }
                                                
                                                                const timeKey = dataPoint.x; // "2024" for yearly or "2024-09" for monthly
                                                
                                                                // Only process data for the specific time period of the hovered bar
                                                                if (timeKey === currentTime) {
                                                                    // Initialize group if it doesn't exist for the specific month/year
                                                                    if (!groupedData[timeKey]) {
                                                                        groupedData[timeKey] = {
                                                                            sumEnergyYields: 0,
                                                                            totalSystemCapacity: 0, // Sum the system capacities of selected facilities
                                                                            totalInsolation: 0, // To be set once per month
                                                                            facilityCount: 0,
                                                                            insolationSet: false // Track if insolation is already set for the month
                                                                        };
                                                                    }                                                
                                                                    // Accumulate energy yields and system capacity for selected facilities
                                                                    groupedData[timeKey].sumEnergyYields += dataPoint.y || 0;
                                                                    groupedData[timeKey].totalSystemCapacity += dataPoint.systemCapacity || 0;

                                                                    // Set insolation once for the specific month
                                                                    if (!groupedData[timeKey].insolationSet && dataPoint.insolation) {
                                                                        groupedData[timeKey].totalInsolation = dataPoint.insolation;
                                                                        groupedData[timeKey].insolationSet = true; // Mark as set
                                                                    }                                                
                                                                    groupedData[timeKey].facilityCount++;
                                                                }
                                                            });
                                                        }
                                                    });
                                                
                                                    // Generate result text only for the specific period related to this bar
                                                    let resultText = '';
                                                    const values = groupedData[currentTime];
                                                    if (values) {
                                                        const { sumEnergyYields, totalSystemCapacity, totalInsolation, facilityCount } = values;
                                                        let performanceRatio = 0;
                                                
                                                        // Calculate PR based on available energy yields, consistent system capacity, and monthly insolation
                                                        if (facilityCount > 0 && totalSystemCapacity > 0 && totalInsolation > 0) {
                                                            const totalSystemCapacityInsolation = totalSystemCapacity * totalInsolation;
                                                            performanceRatio = (sumEnergyYields / totalSystemCapacityInsolation) * 100;
                                                
                                                        }
                                                
                                                        // Set default values if necessary
                                                        performanceRatio = performanceRatio || 0;
                                                        const formattedSumEnergyYields = sumEnergyYields !== undefined ? sumEnergyYields : 0;
                                                        // Round to the nearest integer using toFixed(0)
                                                        const roundedPerformanceRatio = parseFloat(performanceRatio.toFixed(0));
                                                        const roundedSumEnergyYields = parseFloat(formattedSumEnergyYields.toFixed(0));

                                                        // Format result text for the specific period with rounded values
                                                        resultText += `${roundedPerformanceRatio}% PR<br>${Number(roundedSumEnergyYields).toLocaleString()} kWh<br>`;
                                                    }
                                                
                                                    return resultText;
                                                },                                                                                    
                                            }
                                        },
                                        { // Secondary yAxis for insolation
                                            min: 0, // Start from 0
                                            title: {
                                                text: yAxisTitle, // title for the secondary yAxis
                                            },
                                            opposite: true,
                                            labels: {
                                                align: 'left', // Align labels to the left
                                                x: -25, // Adjust the x position if needed
                                                y: 0, // Adjust the y position if needed
                                                style: {
                                                    textOverflow: 'none', // Ensure the text doesn't overflow
                                                    whiteSpace: 'nowrap' // Prevent wrapping
                                                },
                                                formatter: function () {
                                                    return this.value.toLocaleString(); // Format yAxis labels with commas
                                                }
                                            }
                                        }
                                    ],
                                    tooltip: {
                                        formatter: function () {
                                            return `<b>${this.x}</b><br/>${this.series.name}: ${Number(this.y).toLocaleString()} ${this.series.name === 'Insolation' ? yAxisTitle : 'kWh'}`;
                                        },
                                    },
                                    plotOptions: {
                                        column: {
                                            grouping: true,
                                            stacking: 'normal', // Enable stacking
                                            groupPadding: 0.42, // control group spacing
                                            pointPadding: 0.40, // control bar spacing
                                            pointWidth: 55,
                                            dataLabels: {
                                                enabled: true,
                                                formatter: function () {
                                                    return this.y.toLocaleString(); // Format data labels with commas
                                                }
                                            }
                                        },
                                        line: {
                                            dashStyle: 'Dash', // Dotted line
                                            dataLabels: {
                                                enabled: true
                                            },
                                            marker: {
                                                symbol: 'circle' // Set the marker symbol to circle
                                            },
                                            enableMouseTracking: true
                                        }
                                    },
                                    series: [
                                        ...this.state.facilitySeries.map((series, index) => ({
                                            ...series,
                                            color: this.getGradientColor(index),
                                            type: 'column',
                                            yAxis: 0, // Assign to primary yAxis
                                        })),
                                        ...this.state.isolationSeries.map(series => ({
                                            ...series,
                                            type: 'line',
                                            yAxis: 1, // Assign to secondary yAxis
                                        }))
                                    ]
                                }}
                            />
                            <div className="disclaimer-wrap">
                                <Tooltip
                                    title={
                                        <div style={{ fontSize: '1rem', color: '#F8F8F8', lineHeight: '1.5em' }}>
                                            {strings.lblStatisticsDisclaimer}
                                        </div>
                                    }
                                >
                                    <InfoIcon className="disclaimer-icon" />
                                </Tooltip>
                            </div>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    selectedStationName: statisticsReducer.getSelectedStatisticsStationName(state),
    stationTimeOffset: statisticsReducer.getStationTimeOffset(state),
    statisticsTotalize: statisticsReducer.getStatisticsTotalize(state),
    otherData: statisticsReducer.getOtherData(state),
    statisticsFilter: statisticsReducer.getStatisticsFilter(state),
});

const mapActionsToProps = {
    setselectStatisticsStation: statisticsActionMgr.setSelectStatisticsStation,
    getStationTimeOffset: statisticsActionMgr.getStationTimeOffset,
    setSelectYear: statisticsActionMgr.setSelectYear,
};

export default connect(mapStateToProps, mapActionsToProps)(
    withRouter(StationStatisticsGraph)
);
